import { adsAssistantClientFetch } from "./adsAssistantClientHttp";

export const SSD_SALES_BASE = "/api/ssd/sales";

export type SsdImportJob = {
  id: string;
  filename: string;
  status: string;
  row_count: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  issue_count: number;
  matchable_count: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
};

export type SsdSaleFact = {
  id: string;
  sale_date: string | null;
  customer_full_name: string;
  customer_email: string;
  customer_phone: string;
  gross_sale_amount: number | null;
  source_dealer_name: string;
  order_number?: string;
  status: string;
  email_valid: boolean;
  phone_valid: boolean;
  is_new?: boolean;
};

export type { OrgSalesAggregation } from "@/lib/ssd/ssdSalesAggregation";

export type SsdFeedState = {
  reviewed_through: string | null;
  new_count: number;
};

export type SsdInboundEvent = {
  id: string;
  event_type: string;
  processing_status: string;
  created_at: string;
  error_message: string | null;
  dealer_name?: string;
  order_number?: string;
  subject?: string;
};

export type SsdInboundSnapshot = {
  events: SsdInboundEvent[];
  by_type: Array<{ event_type: string; count: number }>;
};

export type SsdUnmappedDealer = {
  source_dealer_id: string;
  source_dealer_name: string;
  fact_count: number;
  selling_org_id: string | null;
  suggested_org_id: string | null;
  suggested_location_id: string | null;
  suggested_action: "create" | "match" | null;
  street: string;
  city: string;
  state: string;
  postal_code: string;
};

export type SsdMapTargetOrg = {
  org_id: string;
  name: string;
  locations: Array<{ id: string; name: string; city: string | null }>;
};

export type SsdDealerMapBoard = {
  unmapped: SsdUnmappedDealer[];
  mapped?: SsdMappedDealer[];
  subscribers: SsdMapTargetOrg[];
  can_release?: boolean;
  manufacturer_org_id?: string | null;
  manufacturer_name?: string | null;
};

export type SsdMappedDealer = {
  source_dealer_id: string;
  source_dealer_name: string;
  selling_org_id: string | null;
  selling_org_name: string | null;
  location_id: string | null;
  location_name: string | null;
  fact_count: number;
};

export type SsdConsentLink = {
  relationship_id: string;
  role: "subscriber" | "manufacturer";
  counterpart_org_id: string;
  counterpart_name: string;
  share_end_customers: boolean;
};

export type SsdExportJob = {
  id: string;
  destination: string;
  date_from: string | null;
  date_to: string | null;
  filename: string;
  row_count: number;
  matchable_count: number;
  created_at: string;
};

export type SsdQualitySummary = {
  total: number;
  matchable: number;
  non_matchable: number;
  missing_email: number;
  invalid_email: number;
  missing_phone: number;
  invalid_phone: number;
  missing_zip: number;
  duplicate_email: number;
  duplicate_phone: number;
};

export type SsdQualityIssue = {
  id: string;
  sale_fact_id: string;
  source_sale_id: string;
  sale_date: string | null;
  customer_full_name: string;
  source_dealer_name: string;
  issue_type: string;
  field: string;
  severity: string;
  raw_value: string;
  status: string;
  source: "ingest" | "derived";
};

export type SsdGoogleConversions = {
  names: string[];
  selected: string;
};

export type SsdDestinationFeed = {
  id: string;
  destination: "google" | "meta";
  token_suffix: string;
  is_active: boolean;
  last_fetched_at: string | null;
  fetch_count: number;
  path: string;
};

export const SSD_EXPORT_BASE = "/api/ssd/export";

export async function fetchSsdSales(params?: { dateFrom?: string; dateTo?: string; limit?: number }) {
  const q = new URLSearchParams();
  if (params?.dateFrom) q.set("date_from", params.dateFrom);
  if (params?.dateTo) q.set("date_to", params.dateTo);
  if (params?.limit) q.set("limit", String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return adsAssistantClientFetch<{
    ok: boolean;
    facts: SsdSaleFact[];
    jobs: SsdImportJob[];
    consent: SsdConsentLink[];
    exports: SsdExportJob[];
    quality: SsdQualitySummary;
    issues: SsdQualityIssue[];
    feed: SsdFeedState;
    inbound: SsdInboundSnapshot;
    dealer_map: SsdDealerMapBoard;
    google_conversions: SsdGoogleConversions;
    destination_feeds: SsdDestinationFeed[];
    aggregation: import("@/lib/ssd/ssdSalesAggregation").OrgSalesAggregation;
  }>(`${SSD_SALES_BASE}${suffix}`);
}

export async function ingestSsdSalesCsv(csvText: string, filename?: string) {
  return adsAssistantClientFetch<{ ok: boolean; job: SsdImportJob }>(SSD_SALES_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ csv_text: csvText, filename: filename ?? "" }),
  });
}

export async function patchSsdShareEndCustomers(relationshipId: string, shareEndCustomers: boolean) {
  return adsAssistantClientFetch<{ ok: boolean; link: SsdConsentLink }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ relationship_id: relationshipId, share_end_customers: shareEndCustomers }),
  });
}

export async function fetchSsdDealerMap() {
  return adsAssistantClientFetch<{
    ok: boolean;
    dealer_map: SsdDealerMapBoard;
  }>(`${SSD_SALES_BASE}?view=dealer_map`);
}

export async function mapSsdDealer(input: {
  source_dealer_id: string;
  source_dealer_name: string;
  selling_org_id: string;
  location_id?: string | null;
    create_location?: {
      name: string;
      address_1?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    } | null;
  not_our_location?: boolean;
}) {
  return adsAssistantClientFetch<{ ok: boolean; mapped: number; location_id: string | null }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ map_dealer: input }),
  });
}

export async function unmapSsdDealer(sourceDealerId: string) {
  return adsAssistantClientFetch<{ ok: boolean; unmapped: number }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ unmap_dealer: { source_dealer_id: sourceDealerId } }),
  });
}

export async function patchSsdDestinationFeed(destination: "google" | "meta") {
  return adsAssistantClientFetch<{ ok: boolean; feed: SsdDestinationFeed; token: string }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ destination_feed: { destination, rotate: true } }),
  });
}

export async function patchSsdGoogleConversionNames(input: {
  selected?: string;
  add?: string;
  names?: string[];
}) {
  return adsAssistantClientFetch<{ ok: boolean; google_conversions: SsdGoogleConversions }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ google_conversion_names: input }),
  });
}

export async function acknowledgeSsdNewFacts() {
  return adsAssistantClientFetch<{ ok: boolean; reviewed_through: string; new_count: number }>(SSD_SALES_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ acknowledge_new: true }),
  });
}

export async function exportSsdAudienceCsv(params: {
  destination: "google" | "meta";
  dateFrom?: string;
  dateTo?: string;
  conversionName?: string;
}) {
  return adsAssistantClientFetch<{
    ok: boolean;
    csv: string;
    filename: string;
    row_count: number;
    job: SsdExportJob | null;
  }>(SSD_EXPORT_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      destination: params.destination,
      date_from: params.dateFrom ?? "",
      date_to: params.dateTo ?? "",
      conversion_name: params.conversionName ?? "",
    }),
  });
}

export function triggerCsvDownload(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
